import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { createOrder } from '../api/orderApi';
import '../styles/Checkout.css';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobile: '',
    area: '',
    blockNumber: '',
    floorNumber: '',
    plotNumber: ''
  });
  const [loading, setLoading] = useState(false);

  // Pre-fill user data from logged-in user
  useEffect(() => {
    if (user) {
      setFormData(prevData => ({
        ...prevData,
        fullName: user.name || '',
        email: user.email || '',
        mobile: user.mobile || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Normalize unit names
  const normalizeUnit = (unit) => {
    if (!unit) return unit;
    // Convert "gm" to "gms"
    if (unit.toLowerCase() === 'gm') return 'gms';
    return unit;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get current date in DD/MM/YYYY format
      const currentDate = new Date();
      const orderDate = `${String(currentDate.getDate()).padStart(2, '0')}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${currentDate.getFullYear()}`;

      // Create full address string
      const fullAddress = `${formData.area}, Block ${formData.blockNumber}, Floor ${formData.floorNumber}, Plot ${formData.plotNumber}`;

      // Prepare products array - only product data
      const products = cart.map(item => {
        const productData = {
          productId: item._id || item.id,
          productName: item.productName || item.name,
          quantity: item.quantity,
          price: item.price
        };
        
        // Only include variant data if a variant is selected
        if (item.selectedVariant?._id) {
          productData.variantId = item.selectedVariant._id;
          productData.variantWeight = item.selectedVariant.weight;
          productData.variantUnit = normalizeUnit(item.selectedVariant.unit);
        }
        
        return productData;
      });

      // Prepare order data with customer details at top level
      const orderData = {
        userId: user?.id || user?._id || '',
        customerName: formData.fullName,
        customerEmail: formData.email,
        customerPhone: formData.mobile,
        customerAddress: fullAddress,
        orderDate: orderDate,
        status: 'Pending',
        products: products
      };

      // Send order in a single API call
      await createOrder(orderData);

      alert('Order placed successfully!');
      clearCart();
      navigate('/');
    } catch (error) {
      console.error('Error creating order:', error);
      
      if (error.response) {
        alert(`Order failed: ${error.response.data?.message || 'Please try again'}`);
      } else if (error.request) {
        alert('Cannot connect to server. Please check if the backend is running.');
      } else {
        alert('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="checkout-page">
      <h1>Checkout</h1>
      
      <div className="checkout-container">
        <form onSubmit={handleSubmit} className="checkout-form">
          <h2>Billing Information</h2>
          
          <div className="form-group">
            <label>Full Name *</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
            {user && formData.fullName && (
              <small className="field-info">✓ Pre-filled from your account</small>
            )}
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email"
            />
            {user && formData.email && (
              <small className="field-info">✓ Pre-filled from your account</small>
            )}
          </div>

          <div className="form-group">
            <label>Mobile Number *</label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              required
              placeholder="Enter your mobile number"
              pattern="[0-9]{10,15}"
              title="Please enter a valid mobile number (10-15 digits)"
            />
            {user && formData.mobile && (
              <small className="field-info">✓ Pre-filled from your account</small>
            )}
          </div>

          <div className="form-group">
            <label>Area *</label>
            <select
              name="area"
              value={formData.area}
              onChange={handleChange}
              required
            >
              <option value="">Select Area</option>
              <option value="Aditya Empress Heights">Aditya Empress Heights</option>
              <option value="Provident Kenworth">Provident Kenworth</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Block Number *</label>
              <input
                type="text"
                name="blockNumber"
                value={formData.blockNumber}
                onChange={handleChange}
                required
                placeholder="Enter block number"
              />
            </div>

            <div className="form-group">
              <label>Floor Number *</label>
              <input
                type="text"
                name="floorNumber"
                value={formData.floorNumber}
                onChange={handleChange}
                required
                placeholder="Enter floor number"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Plot Number *</label>
            <input
              type="text"
              name="plotNumber"
              value={formData.plotNumber}
              onChange={handleChange}
              required
              placeholder="Enter plot number"
            />
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Processing...' : 'Place Order'}
          </button>
        </form>

        <div className="order-summary">
          <h2>Order Summary</h2>
          {cart.map(item => {
            const itemId = item._id || item.id;
            const itemName = item.productName || item.name;
            return (
              <div key={itemId} className="summary-item">
                <span>{itemName} x {item.quantity}</span>
                <span>₹{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            );
          })}
          <div className="summary-total">
            <strong>Total Amount:</strong>
            <strong>₹{getCartTotal().toFixed(2)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
